import React, { useState, useEffect } from 'react';

/**
 * ReviewModal - The "frictionless capture" modal that appears when 
 * background photo monitoring detects new baby photos.
 * 
 * Features:
 * - Photo selection (max 5) for mass discoveries
 * - Voice recording with hold-to-record UX
 * - Text entry for memory description
 * 
 * Props:
 *   - isOpen: boolean - controls visibility
 *   - photos: string[] - array of photo IDs from Capacitor bridge
 *   - onClose: () => void - callback to close modal
 *   - onSave: (data: { text: string, photoIds: string[], timestamp: string }) => Promise<void>
 *   - handleStartRecording: () => Promise<void>
 *   - handleStopRecording: () => Promise<void>
 *   - isRecording: boolean
 *   - audioBase64: string | null - the captured audio data
 */

const MAX_PHOTO_SELECTION = 5;

const ReviewModal = ({ 
  isOpen, 
  photos = [], 
  onClose, 
  onSave,
  handleStartRecording, 
  handleStopRecording, 
  isRecording,
  audioBase64 
}) => {
  // Local state
  const [entryText, setEntryText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

  // Auto-select all photos if 5 or fewer, otherwise require manual selection
  useEffect(() => {
    if (photos.length <= MAX_PHOTO_SELECTION) {
      setSelectedIds(new Set(photos));
    } else {
      setSelectedIds(new Set());
    }
  }, [photos]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEntryText("");
      setSelectedIds(new Set());
      setSaveProgress({ current: 0, total: 0 });
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) return null;

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else if (newSet.size < MAX_PHOTO_SELECTION) {
      newSet.add(id);
    }
    // If at max, don't add more (could show a toast here)
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    // Select first 5 (or all if fewer)
    const toSelect = photos.slice(0, MAX_PHOTO_SELECTION);
    setSelectedIds(new Set(toSelect));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleSave = async () => {
    // Must have at least one photo selected OR text/audio
    if (selectedIds.size === 0 && !entryText.trim() && !audioBase64) {
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave?.({
        text: entryText,
        photoIds: Array.from(selectedIds),
        timestamp: new Date().toISOString()
      });
      // Reset local state after successful save
      setEntryText("");
      setSelectedIds(new Set());
      onClose();
    } catch (err) {
      console.error("Failed to save entry:", err);
      // Could show error toast here
    } finally {
      setIsSaving(false);
      setSaveProgress({ current: 0, total: 0 });
    }
  };

  const needsSelection = photos.length > MAX_PHOTO_SELECTION;
  const canSave = selectedIds.size > 0 || entryText.trim() || audioBase64;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) onClose();
      }}
    >
      <div 
        className="bg-white w-full max-w-lg rounded-t-3xl shadow-2xl"
        style={{ maxHeight: '90vh', animation: 'slideUp 0.3s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white rounded-t-3xl z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              ✨ New Moments Captured
            </h3>
            {needsSelection && (
              <p className="text-xs text-ocean-600 mt-0.5">
                Select up to {MAX_PHOTO_SELECTION} photos to save
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
          >
            ×
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {/* Photo Grid Section */}
          <div className="p-4 border-b bg-gray-50">
            {/* Selection Controls (only show for mass discoveries) */}
            {needsSelection && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">
                  {selectedIds.size}/{MAX_PHOTO_SELECTION} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={clearSelection}
                    className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                  >
                    Clear
                  </button>
                  <button
                    onClick={selectAll}
                    className="text-xs px-2 py-1 rounded bg-ocean-100 hover:bg-ocean-200 text-ocean-700"
                  >
                    Select First 5
                  </button>
                </div>
              </div>
            )}

            {/* Photo Grid */}
            <div className={`grid gap-2 ${
              photos.length <= 3 ? 'grid-cols-3' : 
              photos.length <= 6 ? 'grid-cols-3' : 'grid-cols-4'
            }`}>
              {photos.length > 0 ? (
                photos.map((id, index) => {
                  const isSelected = selectedIds.has(id);
                  const isDisabled = !isSelected && selectedIds.size >= MAX_PHOTO_SELECTION;
                  
                  return (
                    <div 
                      key={id}
                      onClick={() => !isDisabled && toggleSelection(id)}
                      className={`
                        relative aspect-square rounded-lg overflow-hidden cursor-pointer
                        transition-all duration-150
                        ${isSelected 
                          ? 'ring-3 ring-ocean-500 ring-offset-2 scale-95' 
                          : isDisabled 
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:scale-95 hover:ring-2 hover:ring-gray-300'
                        }
                      `}
                    >
                      <img 
                        src={`capacitor://photos-library?id=${id}`} 
                        className="w-full h-full object-cover bg-gray-200"
                        alt={`Photo ${index + 1}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden w-full h-full items-center justify-center bg-gray-200 text-gray-400"
                        style={{ display: 'none' }}
                      >
                        📷
                      </div>
                      
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-6 h-6 bg-ocean-500 rounded-full flex items-center justify-center shadow-md">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      
                      {/* Number Badge for Mass Selection */}
                      {needsSelection && isSelected && (
                        <div className="absolute bottom-1 left-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">
                            {Array.from(selectedIds).indexOf(id) + 1}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="col-span-3 text-center text-gray-400 py-8">
                  No photos detected
                </div>
              )}
            </div>

            {/* Photo Count */}
            <p className="text-xs text-gray-500 mt-3 text-center">
              {photos.length} photo{photos.length !== 1 ? 's' : ''} discovered
              {needsSelection && ` • Tap to select (max ${MAX_PHOTO_SELECTION})`}
            </p>
          </div>

          {/* Input Area */}
          <div className="p-4 space-y-4">
            {/* Text Input */}
            <textarea
              placeholder="What was happening in this moment?"
              value={entryText}
              onChange={(e) => setEntryText(e.target.value)}
              disabled={isSaving}
              className="w-full h-20 p-3 border border-gray-200 rounded-xl resize-none 
                focus:outline-none focus:ring-2 focus:ring-ocean-400 focus:border-transparent 
                text-gray-700 placeholder-gray-400 disabled:bg-gray-50 disabled:opacity-70"
            />
            
            {/* Voice Recording Button */}
            <div className="flex flex-col items-center gap-2">
              <button
                onTouchStart={!isSaving ? handleStartRecording : undefined}
                onTouchEnd={!isSaving ? handleStopRecording : undefined}
                onMouseDown={!isSaving ? handleStartRecording : undefined}
                onMouseUp={!isSaving ? handleStopRecording : undefined}
                onMouseLeave={() => {
                  if (isRecording && !isSaving) handleStopRecording();
                }}
                disabled={isSaving}
                className={`
                  w-14 h-14 rounded-full flex items-center justify-center 
                  transition-all duration-200 shadow-lg
                  ${isSaving 
                    ? 'bg-gray-300 cursor-not-allowed'
                    : isRecording 
                      ? 'bg-red-500 scale-110 animate-pulse' 
                      : 'bg-gradient-to-br from-ocean-400 to-ocean-600 hover:scale-105 active:scale-95'
                  }
                `}
              >
                <span className="text-xl">
                  {isRecording ? '👂' : '🎤'}
                </span>
              </button>
              <p className={`text-xs font-medium transition-colors ${
                isRecording ? 'text-red-500' : 'text-gray-400'
              }`}>
                {isRecording ? 'Recording...' : 'Hold to Record'}
              </p>
              
              {/* Audio Captured Indicator */}
              {audioBase64 && !isRecording && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <span>✓</span> Voice memory captured
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Save Button - Fixed at Bottom */}
        <div className="p-4 border-t bg-white">
          {/* Progress Indicator */}
          {isSaving && saveProgress.total > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Uploading photos...</span>
                <span>{saveProgress.current}/{saveProgress.total}</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-ocean-500 transition-all duration-300"
                  style={{ width: `${(saveProgress.current / saveProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          <button 
            onClick={handleSave}
            disabled={isSaving || !canSave}
            className={`
              w-full py-3 rounded-xl font-semibold text-white
              transition-all duration-200
              ${isSaving || !canSave
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-ocean-500 to-ocean-600 hover:from-ocean-600 hover:to-ocean-700 shadow-md hover:shadow-lg active:scale-98'
              }
            `}
          >
            {isSaving 
              ? 'Saving...' 
              : `Save${selectedIds.size > 0 ? ` ${selectedIds.size} Photo${selectedIds.size > 1 ? 's' : ''}` : ''} 📖`
            }
          </button>
          
          {/* Helper Text */}
          {!canSave && (
            <p className="text-xs text-center text-gray-400 mt-2">
              Select photos or add a story to save
            </p>
          )}
        </div>
      </div>

      {/* CSS for slide-up animation */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ReviewModal;
